import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

export default function Historial() {
  const { usuario } = useAuth()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0])
  const [periodoFilter, setPeriodoFilter] = useState('dia')
  const [edificioFilter, setEdificioFilter] = useState('all')
  const [edificios, setEdificios] = useState([])
  const [total, setTotal] = useState(0)

  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificio_id

  useEffect(() => {
    if (isAdmin) fetchEdificios()
    fetchLogs()
  }, [dateFilter, periodoFilter, edificioFilter])

  const fetchEdificios = async () => {
    const { data } = await supabase.from('edificios').select('*')
    setEdificios(data || [])
  }

  const fetchLogs = async () => {
    setLoading(true)
    const base = new Date(dateFilter)
    let start, end
    if (periodoFilter === 'dia') {
      start = `${dateFilter}T00:00:00`
      end = `${dateFilter}T23:59:59`
    } else if (periodoFilter === 'mes') {
      const y = base.getFullYear(), m = base.getMonth()
      start = new Date(y, m, 1).toISOString()
      end = new Date(y, m + 1, 0, 23, 59, 59).toISOString()
    } else if (periodoFilter === 'anio') {
      const y = base.getFullYear()
      start = new Date(y, 0, 1).toISOString()
      end = new Date(y, 11, 31, 23, 59, 59).toISOString()
    }

    let q = supabase
      .from('accesos')
      .select('*, personas(*), oficinas(numero, titular), edificios(nombre)', { count: 'exact' })
      .gte('timestamp', start)
      .lte('timestamp', end)
      .order('timestamp', { ascending: false })

    if (edificioId) q = q.eq('edificio_id', edificioId)
    else if (edificioFilter !== 'all') q = q.eq('edificio_id', edificioFilter)

    const { data, count } = await q
    setLogs(data || [])
    setTotal(count || 0)
    setLoading(false)
  }

  const filtered = logs.filter(l => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      l.personas?.nombre?.toLowerCase().includes(q) ||
      l.personas?.apellido?.toLowerCase().includes(q) ||
      l.personas?.dni?.includes(q) ||
      l.oficinas?.titular?.toLowerCase().includes(q) ||
      l.oficinas?.numero?.toLowerCase().includes(q)
    )
  })

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  const formatDate = (ts) => new Date(ts).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })

  const periodoLabel = () => {
    const base = new Date(dateFilter + 'T12:00:00')
    if (periodoFilter === 'dia') return base.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
    if (periodoFilter === 'mes') return base.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    return base.getFullYear().toString()
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Historial de Accesos</h1>
        <p style={{ color: 'var(--text3)', fontSize: 14 }}>
          {total} ingreso{total !== 1 ? 's' : ''} · {periodoLabel()}
        </p>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Periodo */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[['dia','Día'],['mes','Mes'],['anio','Año']].map(([k,l]) => (
            <button key={k} className={`btn btn-sm ${periodoFilter === k ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setPeriodoFilter(k)}>{l}</button>
          ))}
        </div>
        {/* Input fecha según periodo */}
        <input className="input"
          type={periodoFilter === 'anio' ? 'number' : periodoFilter === 'mes' ? 'month' : 'date'}
          value={periodoFilter === 'anio' ? new Date(dateFilter).getFullYear() : periodoFilter === 'mes' ? dateFilter.slice(0,7) : dateFilter}
          onChange={e => {
            if (periodoFilter === 'anio') setDateFilter(`${e.target.value}-01-01`)
            else if (periodoFilter === 'mes') setDateFilter(`${e.target.value}-01`)
            else setDateFilter(e.target.value)
          }}
          style={{ width: periodoFilter === 'anio' ? 100 : 160 }}
        />
        {/* Edificio (admin) */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn btn-sm ${edificioFilter === 'all' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter('all')}>Todos</button>
            {edificios.map(e => (
              <button key={e.id} className={`btn btn-sm ${edificioFilter === e.id ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter(e.id)}>
                {e.nombre.replace('Essence ', '')}
              </button>
            ))}
          </div>
        )}
      </div>
      <div style={{ marginBottom: 24 }}>
        <input className="input" placeholder="Filtrar por nombre, DNI u oficina..."
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', maxWidth: 400 }} />
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isAdmin
            ? (periodoFilter !== 'dia' ? '100px 80px 1fr 1fr 1fr 120px' : '80px 1fr 1fr 1fr 120px')
            : (periodoFilter !== 'dia' ? '100px 80px 1fr 1fr 1fr' : '80px 1fr 1fr 1fr'),
          padding: '12px 20px', borderBottom: '1px solid var(--border)',
          fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1
        }}>
          {periodoFilter !== 'dia' && <span>FECHA</span>}
          <span>HORA</span><span>PERSONA</span><span>DNI</span><span>OFICINA</span>
          {isAdmin && <span>EDIFICIO</span>}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text3)' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text3)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div>Sin registros para este período</div>
          </div>
        ) : filtered.map((log, i) => (
          <div key={log.id} style={{
            display: 'grid',
            gridTemplateColumns: isAdmin
              ? (periodoFilter !== 'dia' ? '100px 80px 1fr 1fr 1fr 120px' : '80px 1fr 1fr 1fr 120px')
              : (periodoFilter !== 'dia' ? '100px 80px 1fr 1fr 1fr' : '80px 1fr 1fr 1fr'),
            padding: '13px 20px',
            borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
            alignItems: 'center',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
          >
            {periodoFilter !== 'dia' && (
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>
                {formatDate(log.timestamp)}
              </span>
            )}
            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: 'var(--green)' }}>
              {formatTime(log.timestamp)}
            </span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {log.personas?.nombre} {log.personas?.apellido}
            </span>
            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: 'var(--text3)' }}>
              {log.personas?.dni}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              {log.oficinas?.numero} · {log.oficinas?.titular}
            </span>
            {isAdmin && (
              <span style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace' }}>
                {log.edificios?.nombre?.replace('Essence ', '')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
